import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { CaseRealities } from "../../shared/PortfolioTypes";
import {
  costsAvoided,
  TwoRealitiesOutcome,
} from "../../shared/settlementRealities";
import { Breakdown, Metric } from "../general/DataAtoms.tsx";
import { EmptyPanelNote } from "./EmptyPanelNote.tsx";
import { SettlementPanel } from "./SettlementPanel.tsx";

/**
 * What the clients did not have to spend because these cases settled: the
 * total, the average per settled case, and the split by cost type.
 *
 * All three come off one two-world computation (`twoWorlds`, owned by the
 * page) — every case as it resolved against the same case fought to the end —
 * so the total is the breakdown's total by construction.
 */
export const SettlementCostSaved = ({
  realities,
  twoWorlds,
}: {
  realities: CaseRealities[];
  twoWorlds: TwoRealitiesOutcome;
}) => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement",
  });

  const { items, total } = useMemo(() => {
    const items = costsAvoided(twoWorlds).map(({ type, value }) => ({
      label: t(`costType.${type}`),
      value,
    }));
    return {
      items,
      total: items.reduce((sum, i) => sum + i.value, 0),
    };
  }, [twoWorlds, t]);

  // the panel stays even with nothing to show, so the heading of the section
  // above it never sits over a blank area
  const emptyMessage =
    realities.length === 0
      ? t("noBaseline")
      : items.length === 0
        ? t("costSaved.empty")
        : undefined;

  return (
    <SettlementPanel
      label={t("costSaved.title")}
      description={t("costSaved.description")}
    >
      {emptyMessage ? (
        <EmptyPanelNote>{emptyMessage}</EmptyPanelNote>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              columnGap: 3.5,
              rowGap: 2.25,
              mt: 2,
            }}
          >
            <Metric
              label={t("costSaved.total.title")}
              value={currency.format(total)}
              caption={t("costSaved.total.subTitle")}
            />
            <Metric
              label={t("costSaved.avg.title")}
              value={currency.format(total / realities.length)}
              caption={t("costSaved.avg.subTitle")}
            />
          </Box>

          {/* the cost avoided by type: the largest first, each with its
              share of the total */}
          <Box sx={{ mt: 2.25, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography
              variant="subtitle2"
              component="h4"
              sx={{ fontWeight: 700 }}
            >
              {t("costSaved.byType.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.375,
                mb: 1.25,
                color: "text.secondary",
                maxWidth: "124ch",
              }}
            >
              {t("costSaved.byType.description")}
            </Typography>
            <Breakdown items={items} exact lineFrom="lg" />
          </Box>
        </>
      )}
    </SettlementPanel>
  );
};
